import React, { useCallback, useEffect, useMemo, useState } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS, buildHubmedPricingPath } from "@/config/apiConstants";

export const HUBMED_PRICING_FIELDS = [
  { key: "HubmedPrivate", label: "Private" },
  { key: "HubmedCannabisInitial", label: "Cannabis (initial)" },
  { key: "HubmedCannabisRepeat", label: "Cannabis (repeat)" },
  { key: "HubmedWeightLoss", label: "Weight loss" },
  { key: "HubmedADHDInitial", label: "ADHD (initial)" },
  { key: "HubmedADHDRepeat", label: "ADHD (repeat)" },
  { key: "HubmedSmoking", label: "Smoking cessation" },
  { key: "HubmedSpecialistReferral", label: "Specialist referral" },
];

export function parseHubmedPriceValue(raw) {
  if (raw === "" || raw == null) return null;
  const n = Number(String(raw).replace(/^\s+|\s+$/g, ""));
  return Number.isFinite(n) ? n : null;
}

/**
 * Loads, edits, and saves HubMed DocLink pricing for a Pharmacy Halo chemist.
 * Pass chemistId=null to show the empty state only.
 */
export default function HubmedPricingBlock({
  chemistId,
  title = "DocLink pricing",
  emptyHint = "Select a chemist to load pricing.",
  zConfirm = 60,
}) {
  const [pricingValues, setPricingValues] = useState({});
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [loadedChemistId, setLoadedChemistId] = useState(null);
  const [pricingSaveLoading, setPricingSaveLoading] = useState(false);
  const [pricingSaveDone, setPricingSaveDone] = useState(false);
  const [pricingDirty, setPricingDirty] = useState(false);
  const [highPriceModal, setHighPriceModal] = useState(null);

  const fetchPricing = useCallback(async (id) => {
    setPricingLoading(true);
    setPricingError(null);
    try {
      const data = await phappsRequest(buildHubmedPricingPath(id), "GET");
      const next = {};
      for (const { key } of HUBMED_PRICING_FIELDS) {
        next[key] =
          data[key] != null && data[key] !== "" ? String(data[key]) : "";
      }
      setPricingValues(next);
      setLoadedChemistId(data.ChemistID ?? id);
      setPricingSaveDone(false);
      setPricingDirty(false);
    } catch (e) {
      setPricingError(e.message || "Could not load pricing.");
      setPricingValues({});
      setLoadedChemistId(null);
    } finally {
      setPricingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (chemistId == null) {
      setPricingValues({});
      setLoadedChemistId(null);
      setPricingError(null);
      setPricingSaveDone(false);
      setPricingDirty(false);
      setHighPriceModal(null);
      return;
    }
    fetchPricing(chemistId);
  }, [chemistId, fetchPricing]);

  const validation = useMemo(() => {
    const errors = [];
    const high = [];
    for (const { key, label } of HUBMED_PRICING_FIELDS) {
      const raw = pricingValues[key];
      const n = parseHubmedPriceValue(raw);
      if (n === null) {
        errors.push(`${label}: enter a valid amount.`);
        continue;
      }
      if (n <= 0) {
        errors.push(`${label}: must be greater than zero.`);
        continue;
      }
      if (n > 200) high.push(label);
    }
    return { errors, high };
  }, [pricingValues]);

  const submitPricing = async (skipHighCheck) => {
    if (!loadedChemistId || pricingSaveDone || !pricingDirty) return;
    if (validation.errors.length) {
      setPricingError(validation.errors.join(" "));
      return;
    }
    if (validation.high.length && !skipHighCheck) {
      setHighPriceModal(validation.high);
      return;
    }
    setHighPriceModal(null);
    setPricingSaveLoading(true);
    setPricingError(null);
    try {
      const body = { chemistID: loadedChemistId };
      for (const { key } of HUBMED_PRICING_FIELDS) {
        const n = parseHubmedPriceValue(pricingValues[key]);
        body[key] = n.toFixed(2);
      }
      await phappsRequest(API_PATHS.HUBMED_DOCLINK_PRICING, "POST", body);
      setPricingSaveDone(true);
      setPricingDirty(false);
    } catch (e) {
      setPricingError(e.message || "Save failed.");
    } finally {
      setPricingSaveLoading(false);
    }
  };

  if (chemistId == null) {
    return (
      <section className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/50 p-8 text-center">
        <p className="text-sm text-slate-500">{emptyHint}</p>
      </section>
    );
  }

  return (
    <>
      <section className="rounded-2xl border border-slate-200/80 bg-gradient-to-b from-white to-slate-50/80 shadow-sm p-6 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          {title ? (
            <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          ) : null}
          {pricingSaveDone && (
            <span className="text-xs font-medium text-emerald-700 bg-emerald-100/80 px-3 py-1 rounded-full">
              Saved — edit fields to update again
            </span>
          )}
        </div>
        <p className="text-sm text-slate-600">
          Amounts must be greater than zero. Values over $200 require
          confirmation before saving.
        </p>

        {pricingLoading && (
          <p className="text-sm text-slate-500">Loading pricing…</p>
        )}

        {!loadedChemistId && pricingError && (
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-red-100 bg-red-50/80 px-4 py-3">
            <p className="text-sm text-red-700 flex-1">{pricingError}</p>
            <button
              type="button"
              onClick={() => fetchPricing(chemistId)}
              disabled={pricingLoading}
              className="shrink-0 px-4 py-2 rounded-lg bg-white border border-red-200 text-red-800 text-sm font-medium hover:bg-red-50 disabled:opacity-50"
            >
              Retry load
            </button>
          </div>
        )}

        {loadedChemistId != null && (
          <>
            <div className="grid sm:grid-cols-2 gap-4">
              {HUBMED_PRICING_FIELDS.map(({ key, label }) => (
                <label key={key} className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium text-slate-700">{label}</span>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
                      $
                    </span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={pricingValues[key] ?? ""}
                      onChange={(e) => {
                        setPricingValues((p) => ({
                          ...p,
                          [key]: e.target.value,
                        }));
                        setPricingDirty(true);
                        setPricingSaveDone(false);
                        setPricingError(null);
                      }}
                      disabled={pricingSaveLoading}
                      className="w-full rounded-xl border border-slate-200 pl-8 pr-3 py-2.5 text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500/25 focus:border-violet-400 disabled:bg-slate-100"
                    />
                  </div>
                </label>
              ))}
            </div>
            {pricingError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                {pricingError}
              </p>
            )}
            <div className="flex flex-wrap gap-3 pt-2">
              <button
                type="button"
                onClick={() => submitPricing(false)}
                disabled={
                  pricingSaveLoading ||
                  pricingSaveDone ||
                  !pricingDirty ||
                  !loadedChemistId
                }
                className="px-6 py-2.5 rounded-xl bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none transition"
              >
                {pricingSaveLoading ? "Saving…" : "Save pricing"}
              </button>
            </div>
          </>
        )}
      </section>

      {highPriceModal && (
        <div
          className="fixed inset-0 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm"
          style={{ zIndex: zConfirm }}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4 border border-slate-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="hubmed-high-price-title"
          >
            <h3
              id="hubmed-high-price-title"
              className="text-lg font-semibold text-slate-900"
            >
              Confirm high pricing
            </h3>
            <p className="text-sm text-slate-600">
              These services are set above $200:
            </p>
            <ul className="text-sm text-slate-800 list-disc pl-5 space-y-1">
              {highPriceModal.map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            <p className="text-sm text-slate-600">
              Are you sure these amounts are correct?
            </p>
            <div className="flex gap-3 justify-end pt-2">
              <button
                type="button"
                onClick={() => setHighPriceModal(null)}
                className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => submitPricing(true)}
                disabled={pricingSaveLoading}
                className="px-4 py-2 rounded-xl bg-amber-600 text-white font-semibold hover:bg-amber-700 disabled:opacity-50"
              >
                Yes, save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
